import type { ChipsetSnapshot } from '../chipset/Chipset';

interface BufferPlaceholder {
  __buf: number;
  byteOffset?: number;
  length?: number;
}

export interface SerializedSnapshot {
  json: string;
  buffers: ArrayBuffer[];
}

export function serializeSnapshot(snapshot: ChipsetSnapshot): SerializedSnapshot {
  const bufferMap = new Map<ArrayBuffer, number>();
  const buffers: ArrayBuffer[] = [];

  function walk(value: unknown): unknown {
    if (value instanceof Uint8Array) {
      const buf = value.buffer;
      if (buf instanceof ArrayBuffer) {
        let idx = bufferMap.get(buf);
        if (idx === undefined) {
          idx = buffers.length;
          bufferMap.set(buf, idx);
          buffers.push(buf);
        }
        return { __buf: idx, byteOffset: value.byteOffset, length: value.byteLength };
      }
      return value;
    }
    if (value instanceof ArrayBuffer) {
      let idx = bufferMap.get(value);
      if (idx === undefined) {
        idx = buffers.length;
        bufferMap.set(value, idx);
        buffers.push(value);
      }
      return { __buf: idx };
    }
    if (Array.isArray(value)) {
      return value.map(walk);
    }
    if (value !== null && typeof value === 'object') {
      const result: Record<string, unknown> = {};
      for (const key of Object.keys(value)) {
        result[key] = walk((value as Record<string, unknown>)[key]);
      }
      return result;
    }
    return value;
  }

  const skeleton = walk(snapshot);
  return { json: JSON.stringify(skeleton), buffers };
}

export function deserializeSnapshot(data: SerializedSnapshot): ChipsetSnapshot {
  const skeleton = JSON.parse(data.json);

  function walk(value: unknown): unknown {
    if (Array.isArray(value)) {
      return value.map(walk);
    }
    if (value !== null && typeof value === 'object') {
      if ('__buf' in (value as Record<string, unknown>)) {
        const p = value as BufferPlaceholder;
        const buf = data.buffers[p.__buf];
        if (!buf) {
          throw new Error(`Missing buffer at index ${p.__buf}`);
        }
        if (p.byteOffset !== undefined && p.length !== undefined) {
          return new Uint8Array(buf, p.byteOffset, p.length);
        }
        return new Uint8Array(buf);
      }
      const result: Record<string, unknown> = {};
      for (const key of Object.keys(value as Record<string, unknown>)) {
        result[key] = walk((value as Record<string, unknown>)[key]);
      }
      return result;
    }
    return value;
  }

  return walk(skeleton) as ChipsetSnapshot;
}

export function collectTransferables(data: SerializedSnapshot): ArrayBuffer[] {
  return data.buffers;
}

export function isSerializedSnapshot(value: unknown): value is SerializedSnapshot {
  return (
    value !== null &&
    typeof value === 'object' &&
    'json' in (value as Record<string, unknown>) &&
    'buffers' in (value as Record<string, unknown>) &&
    Array.isArray((value as Record<string, unknown>).buffers)
  );
}
